import { useState, useEffect } from "react";
import { Heart, Users, TrendingUp, Gift } from "lucide-react";

const timelineStages = [
  {
    age: "1 year",
    moment: "First Birthday",
    icon: Heart,
    contributions: "€850",
    contributors: 12,
    capitalInvested: "€850",
    capitalFuture: "~€2,347",
    loved_ones: 12,
    color: "from-pink-400 to-pink-600",
  },
  {
    age: "6 years",
    moment: "First Day of School",
    icon: Gift,
    contributions: "€3,200",
    contributors: 25,
    capitalInvested: "€4,050",
    capitalFuture: "~€10,500",
    loved_ones: 35,
    color: "from-blue-400 to-blue-600",
  },
  {
    age: "13 years",
    moment: "Teen Years",
    icon: TrendingUp,
    contributions: "€5,800",
    contributors: 45,
    capitalInvested: "€9,850",
    capitalFuture: "~€21,000",
    loved_ones: 60,
    color: "from-purple-400 to-purple-600",
  },
  {
    age: "18 years",
    moment: "Adulthood",
    icon: Users,
    contributions: "€6,760",
    contributors: 80,
    capitalInvested: "€16,610",
    capitalFuture: "~€41,000",
    growth: "+147%",
    color: "from-growth to-future",
  },
];

const SaCagnotteEn = () => {
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % timelineStages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const currentStage = timelineStages[activeStage];
  const Icon = currentStage.icon;

  return (
    <section className="section-padding bg-gradient-to-b from-white via-purple-50/30 to-pink-50/30">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-legatia-green/20 text-legatia-green rounded-full px-6 py-2 mb-4">
            <span className="text-sm font-semibold">Their savings, milestone by milestone</span>
          </div>
          <h2 className="section-title">
            A memory <span className="text-legatia-green">+ capital</span> at each moment
          </h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            Each life moment becomes a gift that grows over time
          </p>
        </div>

        {/* Interactive Timeline */}
        <div className="max-w-6xl mx-auto">
          {/* Progress Bar */}
          <div className="relative mb-12">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-muted -translate-y-1/2" />
            <div 
              className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 -translate-y-1/2 transition-all duration-700"
              style={{ width: `${((activeStage + 1) / timelineStages.length) * 100}%` }}
            />
            
            <div className="relative flex justify-between">
              {timelineStages.map((stage, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStage(index)}
                  className={`
                    relative z-10 flex flex-col items-center gap-2 transition-all duration-300
                    ${index === activeStage ? 'scale-110' : 'scale-100 opacity-60 hover:opacity-100'}
                  `}
                >
                  <div className={`
                    w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center
                    ${index === activeStage 
                      ? `bg-gradient-to-br ${stage.color} shadow-lg animate-pulse-slow` 
                      : 'bg-muted'
                    }
                    transition-all duration-300
                  `}>
                    <stage.icon className={`h-6 w-6 md:h-8 md:h-8 ${index === activeStage ? 'text-white' : 'text-muted-foreground'}`} />
                  </div>
                  <span className={`
                    text-xs md:text-sm font-semibold whitespace-nowrap
                    ${index === activeStage ? 'text-foreground' : 'text-muted-foreground'}
                  `}>
                    {stage.age}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Display Card */}
          <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12 border-2 border-legatia-green/20 animate-fade-in">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              {/* Left - Moment Info */}
              <div className="space-y-6">
                <div>
                  <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentStage.color} text-white px-4 py-2 rounded-full mb-4`}>
                    <Icon className="h-5 w-5" />
                    <span className="font-semibold">{currentStage.age}</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-2">{currentStage.moment}</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Gift className="h-6 w-6 text-legatia-green" />
                      <span className="font-medium">Gifts received</span>
                    </div>
                    <span className="text-2xl font-bold text-legatia-green">{currentStage.contributions}</span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Users className="h-6 w-6 text-blue-500" />
                      <span className="font-medium">Contributors</span>
                    </div>
                    <span className="text-2xl font-bold">{currentStage.contributors}</span>
                  </div>
                </div>
              </div>

              {/* Right - Financial Growth */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-legatia-green/10 to-future/10 rounded-2xl p-6 border border-legatia-green/20">
                  <h4 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Capital evolution
                  </h4>

                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-muted-foreground">Total invested</span>
                        <span className="text-lg font-bold">{currentStage.capitalInvested}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '60%' }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-muted-foreground">At 18 years</span>
                        <span className="text-2xl font-bold text-legatia-green">{currentStage.capitalFuture}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 h-3 rounded-full animate-pulse-slow" style={{ width: '100%' }} />
                      </div>
                    </div>
                  </div>

                  {currentStage.growth && (
                    <div className="mt-4 pt-4 border-t border-legatia-green/20">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Growth</span>
                        <span className="text-xl font-bold text-legatia-green">{currentStage.growth}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-sm text-foreground/80">
                    <span className="font-semibold text-legatia-green">💡 Key:</span> Each milestone becomes a memory album + invested capital that grows until age 18
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              💡 Based on historical market average of 7-8% annual return (MSCI World Index)
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaCagnotteEn;
